/**
 * Fetch the widget-manifest from the Mhosaic backend.
 *
 * The loader calls this on every page load. The backend reads `Project.
 * pinned_version` (or current stable) + `Project.widget_enabled` and
 * returns the bundle URL + SRI hash for the version this tenant should
 * receive — or `{enabled: false}` if the widget is killed for the tenant.
 *
 * Network failures are surfaced to the caller. The loader degrades to
 * "widget not present" rather than crashing the host page.
 */

export interface Manifest {
  /** False when the widget is disabled for this project (kill switch) or
   *  no stable release exists. Loader bails silently. */
  enabled: boolean
  /** Semver of the bundle being served. Present when enabled=true. */
  version?: string
  /** Fully-qualified URL to the version-pinned bundle (jsDelivr). */
  bundle_url?: string
  /** Subresource integrity hash for the bundle. The loader injects the
   *  script tag with `integrity=…` so the browser refuses to run a
   *  bundle whose bytes don't match. */
  sri_hash?: string
  /** Static config the manifest endpoint passes through to the widget. */
  config?: {
    endpoint: string
    project_slug: string
    share_reports_with_widget: boolean
    /** Phase 4: project gates the widget per end-user (default false). */
    requires_visibility_check?: boolean
    /** Per-project switch for the bundle's default error capture. When the
     *  backend omits it, the bundle falls back to its own default (on). */
    error_tracking?: boolean
  }
  /** Human-readable detail when `enabled` is false. */
  detail?: string
}

export async function fetchManifest(
  endpoint: string,
  apiKey: string,
  signal?: AbortSignal,
): Promise<Manifest> {
  const base = endpoint.replace(/\/$/, '')
  const url = `${base}/api/feedback/v1/widget-manifest/?pk=${encodeURIComponent(apiKey)}`
  // credentials: 'omit' — we authenticate via the public pk_proj_ query
  // param; sending cookies would just trigger CORS preflights for no
  // reason and is unnecessary since the manifest endpoint doesn't read
  // any session.
  const init: RequestInit = { credentials: 'omit' }
  if (signal) init.signal = signal
  const res = await fetch(url, init)
  if (!res.ok) {
    throw new Error(
      `mhosaic-feedback: manifest fetch failed (HTTP ${res.status})`,
    )
  }
  const body = (await res.json()) as Manifest
  return body
}
